import React, { useState } from "react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "src/components/ui/table";
import Checkbox from "src/components/form-new/checkbox";
import BorderedInput from "src/components/form-new/bordered-input";
import { Panel, PanelField, SelectorConfiguration } from "../../types/panel.types";
import BorderedSelect from "src/components/form-new/bordered-select";
import BorderedMultiSelect from "src/components/form-new/bordered-multi-select";
import { useGetPanelScoutUsersQuery } from "../../endpoints/panel-scout-users.endpoints";
import { useGetOrganizationsQuery } from "../../endpoints/panel.endpoints";
import { ChevronDown, ChevronRight, ChevronUp, Lock, Unlock } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "src/components/ui/dialog";
import { Button } from "src/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "src/components/ui/popover";

export interface ColumnData {
  active: boolean;
  name: string;
  samples: string[];
  description: string;
  fillPercentage: number;
}

export interface ParsedCSVData {
  columns: ColumnData[];
  rowCount: number;
  columnCount: number;
}

const UNLOCK_CONFIRM_TEXT = "unlock";

const CONTROL_TYPE_OPTIONS = [
  { value: "single_dropdown", label: "Single dropdown" },
  { value: "multi_select", label: "Multi select" },
  { value: "searchable_multiselect", label: "Searchable multiselect" },
  { value: "text_input", label: "Text input" },
  { value: "number_input", label: "Number input" },
  { value: "range_slider", label: "Range slider" },
  { value: "range_selector_numeric", label: "Range selector (numeric)" },
  { value: "checkbox", label: "Checkbox" },
  { value: "checkbox_list", label: "Checkbox (Multiselect)" },
  // { value: "radio", label: "Radio" },
  { value: "Mutually exclusive small list", label: "Mutually exclusive small list" },
];

function SelectorControlCell({
  field,
  onControlChange,
}: {
  field: PanelField;
  onControlChange?: (merged: SelectorConfiguration) => void;
}) {
  const sc = field.selector_configuration;
  if (!sc) return (
    <span className="text-xs text-gray-400">
      —
    </span>
  );

  const currentControlType = sc.control?.type ?? sc.meta?.suggested_control ?? "";
  const options = sc.options ?? [];
  const range = sc.range;
  const hasRange = range && (range.min != null || range.max != null);

  const handleControlTypeChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newType = e.target.value;
    onControlChange?.({
      ...sc,
      control: { type: newType, selection: sc.control?.selection ?? "single" },
    });
  };

  const isRangeSelectorNumeric = currentControlType === "range_selector_numeric";
  const handleRangeChange = (key: "min" | "max", value: string) => {
    const num = value === "" ? undefined : Number(value);
    if (value !== "" && Number.isNaN(num)) return;
    onControlChange?.({
      ...sc,
      range: { ...(sc.range ?? {}), [key]: num },
    });
  };

  const moveOption = (fromIndex: number, direction: "up" | "down") => {
    const newOptions = [...options];
    const toIndex = direction === "up" ? fromIndex - 1 : fromIndex + 1;
    if (toIndex < 0 || toIndex >= newOptions.length) return;
    [newOptions[fromIndex], newOptions[toIndex]] = [newOptions[toIndex], newOptions[fromIndex]];
    onControlChange?.({ ...sc, options: newOptions });
  };

  const controlOptions =
    currentControlType && !CONTROL_TYPE_OPTIONS.some((o) => o.value === currentControlType)
      ? [{ value: currentControlType, label: currentControlType }, ...CONTROL_TYPE_OPTIONS]
      : CONTROL_TYPE_OPTIONS;

  return (
    <div className="flex flex-col gap-1">
      {onControlChange ? (
        <BorderedSelect
          label=""
          value={currentControlType}
          onChange={handleControlTypeChange}
          options={controlOptions}
          className="min-w-[140px] text-xs"
        />
      ) : (
        currentControlType && (
          <span className="text-xs font-medium text-gray-700">
            {currentControlType}
          </span>
        )
      )}
      {hasRange && (
        <span className="text-xs text-gray-600">
          Range:
          {' '}
          {range!.min != null && range!.max != null
            ? `${range!.min} – ${range!.max}`
            : range!.min != null
              ? `min ${range!.min}`
              : `max ${range!.max}`}
        </span>
      )}
      {isRangeSelectorNumeric && onControlChange && (
        <div className="flex flex-wrap items-center gap-2 mt-1">
          <label className="text-xs text-gray-600 flex items-center gap-1">
            Min:
            <input
              type="number"
              className="w-16 px-1.5 py-0.5 text-xs border border-gray-300 rounded"
              value={range?.min ?? ""}
              onChange={(e) => handleRangeChange("min", e.target.value)}
              onBlur={(e) => handleRangeChange("min", e.target.value)}
              placeholder="—"
            />
          </label>
          <label className="text-xs text-gray-600 flex items-center gap-1">
            Max:
            <input
              type="number"
              className="w-16 px-1.5 py-0.5 text-xs border border-gray-300 rounded"
              value={range?.max ?? ""}
              onChange={(e) => handleRangeChange("max", e.target.value)}
              onBlur={(e) => handleRangeChange("max", e.target.value)}
              placeholder="—"
            />
          </label>
        </div>
      )}
      {options.length > 0 && (
        <Popover>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-gray-600 hover:text-gray-900"
            >
              View options
              {' '}
              <ChevronDown className="w-3 h-3 ml-0.5" />
            </Button>
          </PopoverTrigger>
          <PopoverContent
            className="w-64 max-h-64 overflow-y-auto bg-white border border-gray-200 shadow-lg"
            align="start"
          >
            <div className="text-xs font-medium text-gray-700 mb-2">
              Options
              {' '}
              (
              {options.length}
              )
            </div>
            <ul className="space-y-0.5">
              {options.map((opt, i) => (
                <li
                  key={i}
                  className="flex items-center gap-1 text-xs text-gray-600 group"
                >
                  {onControlChange ? (
                    <span className="flex shrink-0 items-center gap-0.5 opacity-70 group-hover:opacity-100">
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={(e) => { e.stopPropagation(); moveOption(i, "up"); }}
                        disabled={i === 0}
                        aria-label="Move up"
                      >
                        <ChevronUp className="w-3.5 h-3.5" />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-6 w-6 p-0"
                        onClick={(e) => { e.stopPropagation(); moveOption(i, "down"); }}
                        disabled={i === options.length - 1}
                        aria-label="Move down"
                      >
                        <ChevronDown className="w-3.5 h-3.5" />
                      </Button>
                    </span>
                  ) : null}
                  <span className="min-w-0 flex-1 truncate">
                    {opt.label}
                    {opt.label !== opt.value && (
                      <span className="text-gray-400 ml-1">
                        (
                        {opt.value}
                        )
                      </span>
                    )}
                  </span>
                </li>
              ))}
            </ul>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}

function FieldExpandedContent({
  field,
  onDescriptionChange,
}: {
  field: PanelField;
  onDescriptionChange: (value: string) => void;
}) {
  const sc = field.selector_configuration;
  const descriptionValue = field.description ?? "";

  return (
    <div className="space-y-4">
      <Section title="Description">
        <BorderedInput
          className="w-full text-sm"
          value={descriptionValue}
          onBlur={(e) => onDescriptionChange(e.target.value)}
          placeholder="Add description..."
        />
      </Section>
      <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
        <div className="space-y-4">
          <Section title="Sample data">
            {field.sample_data.length === 0 || field.sample_data[0] === "EMPTY" ? (
              <span className="text-xs text-gray-500 italic">
                EMPTY
              </span>
            ) : (
              <div className="flex flex-wrap gap-2">
                {field.sample_data.map((sample, i) => (
                  <span
                    key={i}
                    className="text-xs font-medium text-gray-700 px-2 py-1 rounded bg-gray-200"
                  >
                    {sample}
                  </span>
                ))}
              </div>
            )}
          </Section>
          <Section title="Field info">
            <dl className="text-xs space-y-1 text-gray-700">
              <Row
                label="Name"
                value={field.name}
              />
              <Row
                label="Display name"
                value={field.display_name}
              />
              <Row
                label="Data type"
                value={field.data_type}
              />
              <Row
                label="Fill rate"
                value={`${field.fill_percentage}%`}
              />
              <Row
                label="Status"
                value={field.status}
              />
              <Row
                label="PII"
                value={field.is_pii ? "Yes" : "No"}
              />
              {field.created_at && (
                <Row
                  label="Created"
                  value={field.created_at}
                />
              )}
              {field.updated_at && (
                <Row
                  label="Updated"
                  value={field.updated_at}
                />
              )}
            </dl>
          </Section>
        </div>
        <div className="space-y-4">
          {sc && (
            <>
              <Section title="Selector config (meta)">
                <dl className="text-xs space-y-1 text-gray-700">
                  {sc.meta?.options_total != null && (
                    <Row
                      label="Options total"
                      value={String(sc.meta.options_total)}
                    />
                  )}
                  {sc.meta?.options_truncated != null && (
                    <Row
                      label="Options truncated"
                      value={String(sc.meta.options_truncated)}
                    />
                  )}
                  {sc.meta?.suggested_control && (
                    <Row
                      label="Suggested control"
                      value={sc.meta.suggested_control}
                    />
                  )}
                </dl>
              </Section>
              <Section title="Selector config (stats)">
                <dl className="text-xs space-y-1 text-gray-700">
                  {sc.stats?.total_subjects != null && (
                    <Row
                      label="Total subjects"
                      value={String(sc.stats.total_subjects)}
                    />
                  )}
                  {sc.stats?.distinct_values != null && (
                    <Row
                      label="Distinct values"
                      value={String(sc.stats.distinct_values)}
                    />
                  )}
                  {sc.stats?.non_null_values != null && (
                    <Row
                      label="Non-null values"
                      value={String(sc.stats.non_null_values)}
                    />
                  )}
                </dl>
              </Section>
              {sc.range && (sc.range.min != null || sc.range.max != null) && (
                <Section title="Range">
                  <span className="text-xs text-gray-700">
                    {sc.range.min != null && sc.range.max != null
                      ? `Min: ${sc.range.min}, Max: ${sc.range.max}`
                      : sc.range.min != null
                        ? `Min: ${sc.range.min}`
                        : `Max: ${sc.range.max}`}
                  </span>
                </Section>
              )}
              {(sc.options?.length ?? 0) > 0 && (
                <Section title="Options (full list)">
                  <ul className="text-xs text-gray-700 space-y-0.5 max-h-32 overflow-y-auto">
                    {sc.options!.map((opt, i) => (
                      <li key={i}>
                        {opt.label}
                        {opt.label !== opt.value && ` (${opt.value})`}
                      </li>
                    ))}
                  </ul>
                </Section>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
        {title}
      </h4>
      {children}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex gap-2">
      <dt className="text-gray-500 min-w-[100px]">
        {label}
      </dt>
      <dd className="font-medium">
        {value}
      </dd>
    </div>
  );
}

interface ManagePanelTableProps {
  panel: Panel;
  onPanelChange?: (field: string, value: any) => void;
  onColumnsChange?: (id: string, field: string, value: any) => void;
  onUnlock?: (panelId: string) => void | Promise<void>;
  onLock?: (panelId: string) => void | Promise<void>;
  onAudienceBlur?: (assignmentType: 'organizations' | 'users') => void;
}

const ManagePanelTable: React.FC<ManagePanelTableProps> = ({
  panel,
  onPanelChange,
  onColumnsChange,
  onUnlock,
  onLock,
  onAudienceBlur,
}) => {
  const { fields } = panel;
  const { data: panelScoutUsers = [] } = useGetPanelScoutUsersQuery();
  const { data: organizations = [] } = useGetOrganizationsQuery();
  const [unlockModalOpen, setUnlockModalOpen] = useState(false);
  const [unlockConfirmInput, setUnlockConfirmInput] = useState("");
  const [expandedRowIndex, setExpandedRowIndex] = useState<number | null>(null);

  const toggleRowExpanded = (index: number) => {
    setExpandedRowIndex((prev) => (prev === index ? null : index));
  };

  const handleUnlockClick = () => setUnlockModalOpen(true);
  const handleUnlockModalClose = () => {
    setUnlockModalOpen(false);
    setUnlockConfirmInput("");
  };
  const handleUnlockConfirm = async() => {
    if (unlockConfirmInput !== UNLOCK_CONFIRM_TEXT || !onUnlock) return;
    await onUnlock(panel.id);
    handleUnlockModalClose();
  };
  const canConfirmUnlock = unlockConfirmInput === UNLOCK_CONFIRM_TEXT;

  const handlePanelChange = (field: string, value: any) => {
    if (onPanelChange) {
      onPanelChange(field, value);
    }
  };

  const handleColumnChange = (index: number, field: string, value: any) => {
    if (onColumnsChange) {
      onColumnsChange(fields[index].id, field, value);
    }
  };

  const getFieldInUse = (field: PanelField) => field.status === "active";
  const getFieldActive = (field: PanelField) =>
    field.selector_configuration?.active ?? (field.status === "active");
  const getFieldAdvanced = (field: PanelField) =>
    field.selector_configuration?.advanced ?? field.advanced === true;

  if (fields.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-4 mt-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          {panel.name?.toLowerCase() === "default" ? (
            <span className="text-sm font-medium text-gray-700">
              Default panel
            </span>
          ) : (
            <>
              <BorderedMultiSelect
                label="Assign to Organization"
                className="w-[250px]"
                options={organizations.map((org) => ({ value: org.id, label: org.title }))}
                value={
                  (panel.organizations?.map((o) => o.id)?.length ?? 0) > 0
                    ? (panel.organizations ?? []).map((o) => o.id)
                    : Array.isArray(panel.client)
                      ? panel.client
                      : panel.client
                        ? [panel.client]
                        : []
                }
                onChange={(selectedIds) => {
                  const nextOrgs = selectedIds.map((id) => {
                    const org = organizations.find((o) => o.id === id);
                    return org ? { id: org.id, name: org.title } : { id, name: id };
                  });
                  handlePanelChange("organizations", nextOrgs);
                }}
                onOpenChange={(open) => {
                  if (!open) onAudienceBlur?.('organizations');
                }}
                searchEnabled
                placeholder="Select organizations..."
              />
              <BorderedMultiSelect
                label="Assign to Users"
                className="w-[250px]"
                options={panelScoutUsers.map((u, i) => {
                  const displayName = u.fullname || u.name;
                  const label = displayName ? `${displayName} (${u.email})` : (u.email || "User");
                  const value = u.id || u.email || `user-${i}`;
                  return { value, label };
                })}
                value={(panel.users ?? []).map((u) => (typeof u === "string" ? u : u.id || u.email)).filter(Boolean)}
                onChange={(selectedIds) => {
                  const users = selectedIds
                    .map((id) => {
                      const u = panelScoutUsers.find((x) => x.id === id || x.email === id);
                      if (!u) return null;
                      return { id: u.id || "", email: u.email };
                    })
                    .filter((u): u is { id: string; email: string } => u != null);
                  handlePanelChange("users", users);
                }}
                onOpenChange={(open) => {
                  if (!open) onAudienceBlur?.('users');
                }}
                searchEnabled
                placeholder="Select users..."
              />
            </>
          )}
        </div>
        {panel.locked && onUnlock && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={handleUnlockClick}
            className="flex items-center gap-2"
          >
            <Unlock className="w-4 h-4" />
            Unlock
          </Button>
        )}
        {!panel.locked && onLock && (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => onLock(panel.id)}
            className="flex items-center gap-2"
          >
            <Lock className="w-4 h-4" />
            Lock
          </Button>
        )}
      </div>
      <Dialog
        open={unlockModalOpen}
        onOpenChange={(open) => { setUnlockModalOpen(open); if (!open) setUnlockConfirmInput(""); }}
      >
        <DialogContent className="sm:max-w-md bg-white">
          <DialogHeader>
            <DialogTitle>
              Unlock panel
            </DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Type
            {' '}
            <strong>
              unlock
            </strong>
            {' '}
            below to confirm.
          </p>
          <BorderedInput
            value={unlockConfirmInput}
            onChange={(e) => setUnlockConfirmInput(e.target.value)}
            placeholder="unlock"
            className="w-full"
          />
          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleUnlockModalClose}
            >
              Cancel
            </Button>
            <Button
              type="button"
              onClick={handleUnlockConfirm}
              disabled={!canConfirmUnlock}
            >
              Unlock
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <div className="flex items-center justify-between">
        <div />
        <div className="flex items-center gap-4 text-xs text-gray-500">
          <span>
            <span className="font-medium text-gray-700">
              {fields.filter((f) => getFieldActive(f)).length}
            </span>
            {' '}
            active fields
          </span>
          <span>
            <span className="font-medium text-gray-700">
              {fields.length}
            </span>
            {' '}
            fields
          </span>
          <span>
            <span className="font-medium text-gray-700">
              {panel.panelists || 0}
            </span>
            {' '}
            panelists
          </span>
        </div>
      </div>
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="w-10" />
            <TableHead
              className=""
              title="Use this field (active = used, archived = not used)"
            >
              Use
            </TableHead>
            <TableHead className="">
              Active
            </TableHead>
            <TableHead className="">
              Advanced
            </TableHead>
            <TableHead>
              Column Name
            </TableHead>
            <TableHead>
              Fill Rate
            </TableHead>
            <TableHead>
              Control / Range
            </TableHead>
            <TableHead className="w-full">
              Display Name
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {fields.map((field, index) => {
            const rowDisabled = !getFieldInUse(field);
            return (
              <React.Fragment key={index}>
                <TableRow
                  className="border-b border-gray-200"
                >
                  <TableCell className="py-6 w-10 align-middle">
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      className="h-8 w-8 p-0"
                      onClick={() => toggleRowExpanded(index)}
                      aria-label={expandedRowIndex === index ? "Collapse row" : "Expand row"}
                    >
                      {expandedRowIndex === index ? (
                        <ChevronDown className="w-4 h-4 text-gray-600" />
                      ) : (
                        <ChevronRight className="w-4 h-4 text-gray-600" />
                      )}
                    </Button>
                  </TableCell>
                  <TableCell className="py-6">
                    <Checkbox
                      value={getFieldInUse(field)}
                      onChange={(value) => handleColumnChange(index, "status", value ? "active" : "archived")}
                    />
                  </TableCell>
                  <TableCell className={rowDisabled ? "py-6 relative opacity-60" : "py-6 relative"}>
                    {field.is_pii && (
                      <div className="flex flex-row items-center gap-1 absolute top-2 right-0 border border-gray-200 rounded-full px-1">
                        <div className="text-[8px] text-white bg-red-500 py-[6px] px-[6px] rounded-full" />
                        <span className="text-[10px] text-gray-500">
                          PII
                        </span>
                      </div>
                    )}
                    <Checkbox
                      value={getFieldActive(field)}
                      onChange={(value) => handleColumnChange(index, 'selector_configuration', { ...(field.selector_configuration || {}), active: value })}
                      disabled={rowDisabled}
                    />
                  </TableCell>
                  <TableCell className={rowDisabled ? "py-6 opacity-60" : "py-6"}>
                    <Checkbox
                      value={getFieldAdvanced(field)}
                      onChange={(value) => handleColumnChange(index, 'selector_configuration', { ...(field.selector_configuration || {}), advanced: value })}
                      disabled={rowDisabled}
                    />
                  </TableCell>
                  <TableCell className="py-6 font-semibold w-1/4">
                    {field.name}
                  </TableCell>
                  <TableCell className="py-6">
                    <span
                      className={`
                    inline-flex items-center px-2 py-1 rounded-full text-xs font-medium
                    ${
              field.fill_percentage >= 80
                ? 'bg-green-100 text-green-800'
                : field.fill_percentage >= 50
                  ? 'bg-yellow-100 text-yellow-800'
                  : 'bg-red-100 text-red-800'
              }
                  `}
                    >
                      {field.fill_percentage}
                      %
                    </span>
                  </TableCell>
                  <TableCell className={rowDisabled ? "py-6 max-w-[200px] opacity-60" : "py-6 max-w-[200px]"}>
                    <SelectorControlCell
                      field={field}
                      onControlChange={rowDisabled ? undefined : (merged) => handleColumnChange(index, "selector_configuration", merged)}
                    />
                  </TableCell>
                  <TableCell className={rowDisabled ? "py-6 w-full opacity-60" : "py-6 w-full"}>
                    <BorderedInput
                      className="w-full"
                      value={field.selector_configuration?.field?.display_name ?? field.display_name}
                      onBlur={(e) => handleColumnChange(index, 'selector_configuration', { ...(field.selector_configuration || {}), field: { ...(field.selector_configuration?.field || {}), display_name: e.target.value } })}
                      disabled={rowDisabled}
                    />
                  </TableCell>
                </TableRow>
                {expandedRowIndex === index && (
                  <TableRow className="bg-gray-200/80 border-b border-gray-200">
                    <TableCell
                      colSpan={8}
                      className="py-4 px-6"
                    >
                      <FieldExpandedContent
                        field={field}
                        onDescriptionChange={(value) => handleColumnChange(index, "description", value)}
                      />
                    </TableCell>
                  </TableRow>
                )}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
};

export default ManagePanelTable;
